import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { toast } from "sonner";
import { tx } from "../i18n";

/**
 * Polls `/api/rollover/log` once on mount (per session) and flashes a
 * sonner toast when the overnight automation spawned new recurring items
 * since the user's last visit. Then calls `/acknowledge` so we don't
 * re-notify on every page nav.
 *
 * Kept separate from useRealtimeSync because rollover is rare (≤ 1× per
 * day) and we'd rather not couple it to the WS firehose.
 */
export function useRolloverNotifications() {
  const { user } = useAuth();
  const { lang } = useLang();
  const shownRef = useRef(false);

  useEffect(() => {
    if (!user?.id || shownRef.current) return;
    shownRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const log = await api.get("/rollover/log?limit=5").then((r) => r.data);
        if (cancelled) return;
        const unread = (log || []).filter((e) => !e.acknowledged);
        if (unread.length === 0) return;
        const totals = unread.reduce((acc, e) => ({
          recv: acc.recv + (e.receivables_spawned || 0),
          pay: acc.pay + (e.payments_spawned || 0),
          mov: acc.mov + (e.movements_spawned || 0),
        }), { recv: 0, pay: 0, mov: 0 });
        const parts = [];
        if (totals.recv) parts.push(tx(lang, `${totals.recv} receivable(s)`, `${totals.recv} por cobrar`, `${totals.recv} por cobrar`));
        if (totals.pay) parts.push(tx(lang, `${totals.pay} payment(s)`, `${totals.pay} pago(s)`, `${totals.pay} pago(s)`));
        if (totals.mov) parts.push(tx(lang, `${totals.mov} movement(s)`, `${totals.mov} movimiento(s)`, `${totals.mov} movimiento(s)`));
        const summary = parts.join(", ");
        toast(
          tx(lang, `📅 Auto-rollover: spawned ${summary} for the next period.`, `📅 Roll-over automático: se crearon ${summary} para el próximo período.`, `📅 Roll-over automático: se crearon ${summary} para el próximo período.`),
          {
            duration: 8000,
            description: tx(lang, "Review them on Receivables and Payments.", "Revísalos en Cobros y Pagos.", "Revísalos en Cobros y Pagos."),
          }
        );
        // Mark as read so we don't spam on every page nav.
        await api.post("/rollover/acknowledge");
      } catch (err) {
        // Non-essential UX — log in dev so we notice if the endpoint regresses.
        if (process.env.NODE_ENV !== "production") {
          console.warn("rollover notifications fetch failed", err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, lang]);
}
